import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { resolveStoreName } from '@/lib/display';
import { fetchMarketplaceCategories, type MarketplaceCategory } from '@/lib/marketplaceCatalog';
import AntiBotGuard from '@/components/feature/AntiBotGuard';

export default function VendeurRegisterPage() {
  const { tenant, theme } = useTenant();
  const { registerVendeur, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    nomcommerce: '',
    email: '',
    user_name: '',
    password: '',
    telephone: '',
    ville: '',
    pays: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [humanOk, setHumanOk] = useState(false);
  const [paysList, setPaysList] = useState<Array<{ id: number; nom_pays: string; codepays: number }>>([]);
  const [villeList, setVilleList] = useState<Array<{ id: number; nom_ville: string }>>([]);
  const [paysCode, setPaysCode] = useState<number | null>(null);
  const [categorieId, setCategorieId] = useState<number | null>(null);
  const [sousCategorieId, setSousCategorieId] = useState<number | null>(null);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);

  // Pays : chargés depuis la base de données.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('pays')
        .select('id, nom_pays, codepays')
        .order('nom_pays', { ascending: true });
      if (!cancelled && data) {
        setPaysList((data as Array<{ id: number; nom_pays: string; codepays: number }>).filter((p) => p && p.nom_pays));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Villes : dépendent du pays sélectionné (ville.id_pays = pays.codepays).
  useEffect(() => {
    if (paysCode == null) { setVilleList([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('ville')
        .select('id, nom_ville')
        .eq('id_pays', paysCode)
        .order('nom_ville', { ascending: true });
      if (!cancelled) setVilleList((data as Array<{ id: number; nom_ville: string }>) || []);
    })();
    return () => { cancelled = true; };
  }, [paysCode]);

  // Catégories populaires de la marketplace (mêmes que le site public).
  useEffect(() => {
    if (!tenant?.id) return;
    let cancelled = false;
    (async () => {
      const list = await fetchMarketplaceCategories(Number(tenant.id));
      if (!cancelled) setCategories(list);
    })();
    return () => { cancelled = true; };
  }, [tenant?.id]);

  const handlePaysChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = Number(e.target.value);
    const found = paysList.find((p) => Number(p.codepays) === code);
    setPaysCode(Number.isFinite(code) && code !== 0 ? code : null);
    setForm((f) => ({ ...f, pays: found?.nom_pays || '', ville: '' }));
  };

  useEffect(() => {
    document.title = 'Devenir vendeur';
  }, []);

  // Après création du compte, on ouvre directement le tableau de bord vendeur.
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => navigate('/vendor-dashboard'), 2500);
    return () => window.clearTimeout(t);
  }, [done, navigate]);

  const storeName = resolveStoreName(tenant, theme);

  // Catégories racines (parent_id = 0 ou null) + sous-catégories de la catégorie choisie.
  const isRootCategory = (c: MarketplaceCategory) => c.parent_id == null || Number(c.parent_id) === 0;
  const rootCategories = categories.filter(isRootCategory);
  const subCategoriesFor = categorieId
    ? categories.filter((c) => Number(c.parent_id) === categorieId)
    : [];

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.email.trim() || !form.user_name.trim() || !form.password) {
      setError('Merci de remplir au minimum votre nom, votre email, votre identifiant et votre mot de passe.');
      return;
    }
    if (form.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (!humanOk) {
      setError('Veuillez compléter la vérification anti-robot.');
      return;
    }
    if (!tenant?.id) {
      setError('La marketplace est introuvable.');
      return;
    }
    if (!categorieId) {
      setError('Veuillez sélectionner une catégorie pour votre boutique.');
      return;
    }
    if (subCategoriesFor.length > 0 && !sousCategorieId) {
      setError('Veuillez sélectionner une sous-catégorie.');
      return;
    }

    setSubmitting(true);
    const result = await registerVendeur({
      user_name: form.user_name.trim(),
      email: form.email.trim(),
      password: form.password,
      name: form.name.trim(),
      nomcommerce: form.nomcommerce.trim() || form.name.trim(),
      telephone: form.telephone.trim(),
      Ville: form.ville.trim(),
      Pays: form.pays.trim(),
      marketplaceId: tenant.id,
      categorieId,
      categorieNom: categories.find((c) => c.id === categorieId)?.nom || null,
      souscategorieId: sousCategorieId,
      souscategorieNom: categories.find((c) => c.id === sousCategorieId)?.nom || null,
    });
    setSubmitting(false);

    if (!result.success) {
      setError(result.error || 'Une erreur est survenue.');
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-store-2-line text-3xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-2">
            Votre compte vendeur est créé !
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            Bienvenue sur <strong>{storeName}</strong>. Votre tableau de bord vendeur est prêt : ajoutez vos produits, services et annonces.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/vendor-dashboard"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-dashboard-line"></i>
              Accéder à mon tableau de bord
            </Link>
            <Link
              to="/"
              className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium no-underline hover:bg-background-200/70 transition-colors whitespace-nowrap"
            >
              Retour à la boutique
            </Link>
          </div>
          <p className="mt-4 text-xs text-foreground-400">Redirection automatique vers votre tableau de bord…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-store-2-line text-2xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
            Devenir vendeur
          </h1>
          <p className="text-sm text-foreground-500">
            Créez votre compte vendeur pour proposer vos produits sur {storeName}.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-xl mx-auto">
          {user && (
            <div className="mb-4 px-4 py-3 bg-accent-50 text-accent-700 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-information-line"></i>
              Vous êtes déjà connecté(e) en tant que {user.name}. Vous pouvez créer un compte vendeur distinct ci-dessous.
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="vd-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  id="vd-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Votre nom"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  required
                />
              </div>
              <div>
                <label htmlFor="vd-shop" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nom de la boutique
                </label>
                <input
                  id="vd-shop"
                  name="nomcommerce"
                  type="text"
                  value={form.nomcommerce}
                  onChange={set('nomcommerce')}
                  placeholder="Nom de votre boutique"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div>
              <label htmlFor="vd-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="vd-email"
                name="email"
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="votre@email.com"
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="vd-username" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Identifiant <span className="text-red-500">*</span>
                </label>
                <input
                  id="vd-username"
                  name="user_name"
                  type="text"
                  value={form.user_name}
                  onChange={set('user_name')}
                  placeholder="identifiant unique"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  required
                />
              </div>
              <div>
                <label htmlFor="vd-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <input
                  id="vd-password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={set('password')}
                  placeholder="6 caractères min."
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="vd-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Téléphone
                </label>
                <input
                  id="vd-phone"
                  name="telephone"
                  type="tel"
                  value={form.telephone}
                  onChange={set('telephone')}
                  placeholder="+212..."
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label htmlFor="vd-city" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Ville
                </label>
                <select
                  id="vd-city"
                  name="ville"
                  value={form.ville}
                  onChange={set('ville')}
                  disabled={paysCode == null}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">{paysCode == null ? 'Choisissez d\u2019abord un pays' : 'Sélectionnez une ville'}</option>
                  {villeList.map((v) => (
                    <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="vd-country" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Pays
              </label>
              <select
                id="vd-country"
                name="pays"
                value={paysCode ?? ''}
                onChange={handlePaysChange}
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Sélectionnez un pays</option>
                {paysList.map((p) => (
                  <option key={p.id} value={p.codepays}>{p.nom_pays}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="vd-category" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Catégorie <span className="text-red-500">*</span>
              </label>
              <select
                id="vd-category"
                name="categorie"
                value={categorieId ?? ''}
                onChange={(e) => {
                  setCategorieId(e.target.value ? Number(e.target.value) : null);
                  setSousCategorieId(null);
                }}
                className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                required
              >
                <option value="">Sélectionnez une catégorie</option>
                {rootCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-foreground-400">
                Choisissez la catégorie (produits ou services) qui décrit le mieux votre activité.
              </p>
            </div>

            {/* Sous-catégorie : affichée uniquement si la catégorie en possède. */}
            {subCategoriesFor.length > 0 && (
              <div>
                <label htmlFor="vd-subcategory" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Sous-catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  id="vd-subcategory"
                  name="souscategorie"
                  value={sousCategorieId ?? ''}
                  onChange={(e) => setSousCategorieId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  required
                >
                  <option value="">Sélectionnez une sous-catégorie</option>
                  {subCategoriesFor.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-foreground-400">
                  Précisez la sous-catégorie pour que les clients vous trouvent plus facilement.
                </p>
              </div>
            )}

            {error && (
              <div className="px-4 py-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <AntiBotGuard onValidityChange={setHumanOk} />

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Création du compte...
                </>
              ) : (
                <>
                  <i className="ri-store-2-line"></i>
                  Créer mon compte vendeur
                </>
              )}
            </button>

            <p className="text-xs text-foreground-400 text-center">
              Vous avez déjà un compte ? <Link to="/" className="text-primary-600 no-underline">Retour à la boutique</Link>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}